import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-regular.css";
import { cn } from "../utils/cn.ts";
import { TextLink } from "../components/text-link.tsx";
import { SectionHeader } from "../components/section-header.tsx";
import { FontStyles } from "../utils/font-styles.ts";
import type { ReactNode } from "react";
import { usePropertyDetailsContext, type PropertyDetailsContextValue } from "./contexts/property-details-context.tsx";
import { TrackEvent } from "../utils/analytics.ts";
import { ButtonLink } from "../components/buttons/button-link.tsx";
import { CharteredLogo } from "../solicitors/chartered-logo.tsx";
import { AgentLogoImage } from "../components/agent-logo-image.tsx";

const ITEM_TEXT_STYLE = cn(FontStyles["body-l"], "leading-snug md:leading-snug");

export function hasContact(ctx: PropertyDetailsContextValue): boolean {
  return [
    ctx.websiteContactName ?? ctx.agentName,
    ctx.websiteContactPhone ?? ctx.agentPhone,
    ctx.agentAddress,
    ctx.websiteContactEmail ?? ctx.agentEmail,
    ctx.websiteContactPhotoUrl,
    ctx.agentLogoUrl,
  ].some((value) => !!value);
}

const ContactCard = ({
  imageSlot,
  children,
  className,
}: {
  imageSlot?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      className,
      "rounded overflow-hidden flex flex-col md:flex-row bg-cloud border border-foggy-firth w-full",
    )}
  >
    {imageSlot && (
      <div className="bg-neutral-white min-h-50 md:min-h-0 self-stretch relative md:max-w-80 flex-1 shrink-0 overflow-hidden">
        <div className="absolute h-full w-full flex items-center p-3.5 md:p-5 justify-center">{imageSlot}</div>
      </div>
    )}
    <div
      className={cn(
        "flex-1 min-w-0 flex flex-col",
        imageSlot && "border-t md:border-t-0 md:border-l border-foggy-firth",
      )}
    >
      {children}
    </div>
  </div>
);

// Sales only: rentals show neither button (see showActions in Contact).
const ContactActions = ({ solicitorId, buttonClassName }: { solicitorId?: number; buttonClassName?: string }) => {
  const id = solicitorId !== undefined ? String(solicitorId) : undefined;
  return (
    <>
      <ButtonLink
        href={id ? `/valuations?id=${id}` : "/valuations"}
        variant="outlined"
        colorVariant="secondary"
        className={buttonClassName}
        label="Request valuation"
        onClick={() => {
          TrackEvent({ name: "valuations_clicked", event_source: "property_details_agent_box" });
        }}
        renderLeadingIcon={() => <i aria-hidden="true" className="fa-sharp fa-regular fa-chart-mixed" />}
      />
      <ButtonLink
        href={id ? `/properties?orgid=${id}` : "/properties"}
        variant="outlined"
        colorVariant="secondary"
        className={buttonClassName}
        label="Firm's properties"
        renderLeadingIcon={() => <i aria-hidden="true" className="fa-sharp fa-regular fa-house-chimney" />}
      />
    </>
  );
};

export const Contact = () => {
  const context = usePropertyDetailsContext();
  const {
    agentName,
    agentPhone,
    agentAddress,
    agentEmail,
    agentLogoUrl,
    agentPageUrl,
    websiteContactName,
    websiteContactEmail,
    websiteContactPhotoUrl,
    solicitorId,
    chartered,
    websiteContactPhone,
    displayAddress,
    displayReference,
    rental,
  } = context;

  if (!hasContact(context)) return null;

  const showSecondRow = !!(websiteContactPhotoUrl && agentLogoUrl);
  const showActions = rental === false;
  const contactItems = [];
  const displayingName = websiteContactName ?? agentName;
  if (displayingName) {
    contactItems.push({
      icon: "fa-address-card",
      label: "Name",
      content: <span className={ITEM_TEXT_STYLE}>{displayingName}</span>,
    });
  }
  const displayingPhone = websiteContactPhone ?? agentPhone;
  if (displayingPhone) {
    contactItems.push({
      icon: "fa-phone",
      label: "Phone",
      content: (
        <>
          <TextLink
            href={`tel:${displayingPhone}`}
            className={cn(ITEM_TEXT_STYLE, "inline md:hidden")}
            onClick={() => TrackEvent({ name: "agent_phone_link_click", value: 10 })}
          >
            {displayingPhone}
          </TextLink>
          <span className={cn(ITEM_TEXT_STYLE, "hidden md:inline")}>{displayingPhone}</span>
        </>
      ),
    });
  }
  if (agentAddress) {
    contactItems.push({
      icon: "fa-building",
      label: "Address",
      content: <span className={ITEM_TEXT_STYLE}>{agentAddress}</span>,
    });
  }
  const displayingEmail = websiteContactEmail ?? agentEmail;
  const displayingAddress = displayAddress ?? displayReference ?? "";

  if (displayingEmail) {
    contactItems.push({
      icon: "fa-envelope",
      label: "Email",
      content: (
        <TextLink
          href={`mailto:${displayingEmail}?subject=${displayingAddress} (espc.com)`}
          className={cn(ITEM_TEXT_STYLE, "block overflow-hidden text-ellipsis pb-0.5 whitespace-nowrap")}
          onClick={() => TrackEvent({ name: "agent_mailto_link_click", value: 10 })}
        >
          {displayingEmail}
        </TextLink>
      ),
    });
  }

  const agentLogo = agentLogoUrl ? (
    <AgentLogoImage
      agentLogoUrl={agentLogoUrl}
      agentName={agentName}
      agentPageUrl={agentPageUrl}
      imgClassName="max-h-full"
      linkClassName="flex h-full items-center justify-center"
    />
  ) : undefined;

  const firstRowImage = websiteContactPhotoUrl ? (
    <img src={websiteContactPhotoUrl} className="max-h-full" alt={`Photo of ${displayingName ?? ""}`} />
  ) : (
    agentLogo
  );

  return (
    <section className="flex flex-col gap-2.5 w-full">
      <div className="flex justify-between items-center">
        <SectionHeader
          as="h2"
          className={cn(FontStyles.h5, "lg:hidden")}
          text={showSecondRow ? "Contact" : "Marketed by"}
        />
        <SectionHeader as="h2" className={cn(FontStyles.h5, "hidden lg:block")} text="Contact" />
        {chartered && !showSecondRow && (
          <span className="lg:hidden">
            <CharteredLogo />
          </span>
        )}
      </div>

      <ContactCard imageSlot={firstRowImage}>
        <div className="flex-1 flex flex-col p-3.5 md:p-5">
          <address className="not-italic divide-y divide-foggy-firth">
            {contactItems.map((item) => (
              <div key={item.label} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                <i className={cn("fa-sharp fa-regular text-loch w-4 h-4 my-auto", item.icon)} aria-hidden="true" />
                <span className="sr-only">{item.label}: </span>
                {item.content}
              </div>
            ))}
          </address>

          {showActions && (
            <div
              className={cn(
                showSecondRow ? "hidden lg:flex" : "flex",
                "flex-col xl:flex-row gap-3",
                contactItems.length > 0 && "mt-3 pt-3 border-t border-foggy-firth",
              )}
            >
              <ContactActions solicitorId={solicitorId} buttonClassName="xl:flex-1" />
            </div>
          )}
        </div>
      </ContactCard>

      {showSecondRow && (
        <ContactCard imageSlot={agentLogo} className="flex lg:hidden mt-3.5 md:mt-5.5">
          <div className="flex flex-1 flex-col p-3.5 md:p-5 gap-3">
            {chartered && (
              <div className="flex items-center justify-center">
                <CharteredLogo />
              </div>
            )}
            {showActions && <ContactActions solicitorId={solicitorId} />}
          </div>
        </ContactCard>
      )}
    </section>
  );
};
